export type Order = 'asc' | 'desc';

export interface ColumnVisibility {
  [key: string]: boolean;
}

export interface TableState {
  order: Order;
  orderBy: string;
  selected: string[];
  page: number;
  rowsPerPage: number;
  searchKey: string;
  filterValues: Record<string, any>;
  columnVisibility: ColumnVisibility;
  globalFilter: string;
}

export function createSortComparator<T>(
  order: Order,
  orderBy: keyof T,
  customComparators?: Record<string, (a: any, b: any) => number>
): (a: T, b: T) => number {
  const customComparator = customComparators?.[orderBy as string];
  
  return (a: T, b: T) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];
    
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    
    if (customComparator) {
      const result = customComparator(aValue, bValue);
      return order === 'desc' ? -result : result;
    }
    
    let result = 0;
    if (aValue < bValue) result = -1;
    if (aValue > bValue) result = 1;
    
    return order === 'desc' ? -result : result;
  };
}

export interface ITableMenuAction {
  key: string;
  title: string;
  icon?: React.ReactNode;
  onClick: (selected: string[]) => void | Promise<void>;
  disabled?: (selected: string[]) => boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  confirmDialog?: {
    title: string;
    message: string | ((count: number) => string);
  };
  visible?: (selected: string[]) => boolean;
}

export enum PaginatedTableColumnType {
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  DATETIME = 'datetime',
  BOOLEAN = 'boolean',
  COLORED_CHIP = 'colored_chip',
  LINK = 'link',
  CLICKABLE = 'clickable',
  BARCODE = 'barcode',
  CURRENCY = 'currency',
  ACTION = 'action',
  PHONE_NUMBER = 'phone_number',
  EMAIL = 'email',
  PROGRESS = 'progress',
  RATING = 'rating',
  CUSTOM = 'custom',
  IMAGE = 'image',
  TAGS = 'tags',
  STATUS_BADGE = 'status_badge',
  AVATAR = 'avatar',
  AVATAR_GROUP = 'avatar_group',
  COLOR = 'color',
}

export interface IPaginatedTableColumn {
  id: string;
  label: string;
  type: PaginatedTableColumnType;
  numeric?: boolean;
  disablePadding?: boolean;
  sortable?: boolean;
  hideable?: boolean;
  sticky?: boolean;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  align?: 'left' | 'center' | 'right' | 'justify';
  tooltip?: string | ((row: any) => string);
  exportable?: boolean;
  searchable?: boolean;
  className?: string;
  headerClassName?: string;
  cellClassName?: string | ((row: any) => string);
}

export interface ITextColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.TEXT;
  truncate?: boolean;
  maxLength?: number;
  copyable?: boolean;
}

export interface INumberColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.NUMBER;
  format?: (value: number) => string;
  precision?: number;
  thousandSeparator?: boolean;
}

export interface IDateColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.DATE;
  format?: string;
  relative?: boolean;
}

export interface IDateTimeColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.DATETIME;
  format?: string;
  relative?: boolean;
  showTimezone?: boolean;
}

export interface IBooleanColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.BOOLEAN;
  trueLabel?: string;
  falseLabel?: string;
  trueIcon?: React.ReactNode;
  falseIcon?: React.ReactNode;
  trueColor?: string;
  falseColor?: string;
}

export interface IColoredChipColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.COLORED_CHIP;
  chipColors?: Record<string, {
    color: string;
    backgroundColor: string;
    icon?: React.ReactNode;
  }>;
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'medium';
}

export interface ILinkColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.LINK;
  href: (row: any) => string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  external?: boolean;
}

export interface IClickableColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.CLICKABLE;
  onClick: (id: string, row: any) => void;
  underline?: 'none' | 'hover' | 'always';
}

export interface IBarcodeColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.BARCODE;
  barcodeConfig?: {
    format?: 'CODE128' | 'CODE39' | 'EAN13' | 'EAN8' | 'UPC' | 'CODE93' | 'ITF14' | 'MSI' | 'pharmacode';
    width?: number;
    height?: number;
    displayValue?: boolean;
    fontSize?: number;
    textMargin?: number;
    margin?: number;
  };
}

export interface IColorColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.COLOR;
  shape?: 'square' | 'circle';
  showLabel?: boolean;
}

export interface ICurrencyColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.CURRENCY;
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export interface IActionColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.ACTION;
  actions: Array<{
    key?: string;
    icon: React.ReactNode;
    label: string;
    title?: string;
    onClick: (id: string, row: any) => void | Promise<void>;
    hidden?: (row: any) => boolean;
    disabled?: (row: any) => boolean;
    visible?: (row: any) => boolean;
    color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    confirmDialog?: {
      title: string;
      message: string | ((row: any) => string);
    };
  }>;
  actionsPosition?: 'left' | 'right' | 'center';
  moreActionsLabel?: string;
  maxVisibleActions?: number;
}

export interface IPhoneNumberColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.PHONE_NUMBER;
  countryCode?: string;
  format?: 'international' | 'national' | 'E164';
}

export interface IEmailColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.EMAIL;
  mailtoLink?: boolean;
  obfuscate?: boolean;
}

export interface IProgressColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.PROGRESS;
  min?: number;
  max?: number;
  showPercentage?: boolean;
  showValue?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | ((value: number) => string);
  variant?: 'linear' | 'circular';
  size?: 'small' | 'medium' | 'large';
  expectedField?: string;
  actualField?: string;
  dueDateField?: string;
}

export interface IRatingColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.RATING;
  max?: number;
  precision?: number;
  size?: 'small' | 'medium' | 'large';
  readOnly?: boolean;
  onChange?: (id: string, value: number) => void;
}

export interface ICustomColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.CUSTOM;
  render: (value: any, row: any, id: string) => React.ReactNode;
}

export interface IImageColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.IMAGE;
  width?: number;
  height?: number;
  fallback?: string;
  shape?: 'square' | 'rounded' | 'circular';
  preview?: boolean;
}

export interface ITagsColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.TAGS;
  maxVisible?: number;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
  color?: string | ((tag: string) => string);
}

export interface IStatusBadgeColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.STATUS_BADGE;
  statuses: Record<string, {
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    icon?: React.ReactNode;
    pulse?: boolean;
  }>;
}

export interface IAvatarColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.AVATAR;
  nameField?: string;
  srcField?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'circular' | 'rounded' | 'square';
}

export interface IAvatarGroupColumn extends IPaginatedTableColumn {
  type: PaginatedTableColumnType.AVATAR_GROUP;
  max?: number;
  size?: 'small' | 'medium' | 'large';
  getAvatars: (row: any) => Array<{ name: string; src?: string }>;
}

export interface PaginatedTableColumn {
  id: string;
  label: string;
  type: PaginatedTableColumnType;
  numeric?: boolean;
  disablePadding?: boolean;
  sortable?: boolean;
  hideable?: boolean;
  sticky?: boolean;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  align?: 'left' | 'center' | 'right' | 'justify';
  tooltip?: string | ((row: any) => string);
  exportable?: boolean;
  searchable?: boolean;
  className?: string;
  headerClassName?: string;
  cellClassName?: string | ((row: any) => string);
  editable?: boolean;
  required?: boolean;
  
  chipColors?: Record<string, { backgroundColor: string; color: string }>;
  render?: (value: any, row: any, key: string) => React.ReactNode;
  
  barcodeConfig?: {
    format?: 'CODE128' | 'CODE39' | 'EAN13' | 'EAN8' | 'UPC' | 'CODE93' | 'ITF14' | 'MSI' | 'pharmacode';
    width?: number;
    height?: number;
    displayValue?: boolean;
    fontSize?: number;
    textMargin?: number;
    margin?: number;
  };
  
  actions?: Array<{
    key?: string;
    icon: React.ReactNode;
    label: string;
    title?: string;
    onClick: (id: string, row: any) => void | Promise<void>;
    hidden?: (row: any) => boolean;
    disabled?: (row: any) => boolean;
    visible?: (row: any) => boolean;
    color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  }>;
  
  expectedField?: string;
  actualField?: string;
  dueDateField?: string;
  
  href?: string | ((row: any) => string);
  onClick?: (rowId: string, row: any) => void;
  
  [key: string]: any;
}

export interface ExportOptions {
  filename?: string;
  format?: 'csv' | 'xlsx' | 'json';
  columns?: string[];
  includeHeaders?: boolean;
  dateFormat?: string;
  numberFormat?: string;
}

export interface SelectionOptions {
  showCheckbox?: boolean;
  selectOnRowClick?: boolean;
  selectionMode?: 'single' | 'multiple';
  disableSelection?: (row: any) => boolean;
  onSelectionChange?: (selected: string[]) => void;
}

export interface VirtualizationOptions {
  enabled?: boolean;
  rowHeight?: number | ((row: any) => number);
  overscan?: number;
}

export interface PaginatedTableProps {
  columns: PaginatedTableColumn[];
  data: Record<string, any>;
  idField?: string;
  
  title?: string;
  subtitle?: string;
  loading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
  
  actions?: ITableMenuAction[];
  filters?: ITableFilterType[];
  defaultState?: Partial<TableState>;
  
  selection?: SelectionOptions;
  
  dense?: boolean;
  stickyHeader?: boolean;
  maxHeight?: string | number;
  striped?: boolean;
  bordered?: boolean;
  hoverable?: boolean;
  
  wmsMode?: boolean;
  densityLevel?: 'comfortable' | 'standard' | 'compact';
  boxedCells?: boolean;
  boxedCellClass?: string;
  stripedColors?: string[] | ((theme: any) => string[]);
  
  addingRow?: boolean;
  editingRowId?: string | null;
  editRowValues?: Record<string, any>;
  editRowErrors?: Record<string, string>;
  onEditInputChange?: (colId: string, value: any) => void;
  onEditCancel?: () => void;
  onEditSave?: () => void;
  onEditRowClick?: (rowId: string, row: any) => void;
  addableColumns?: PaginatedTableColumn[];
  addRowAtBottom?: boolean;
  showEditOnTable?: boolean;
  
  actionsColumnId?: string;
  rowActions?: any[];
  rowActionsOutsideMenu?: boolean;
  
  virtualization?: VirtualizationOptions;
  export?: ExportOptions | boolean;
  print?: boolean;
  columnChooser?: boolean;
  fullscreen?: boolean;
  
  // Enhanced pagination features
  infiniteScroll?: boolean;
  showFirstLastButtons?: boolean;
  showPageJumpInput?: boolean;
  pageJumpInputPlaceholder?: string;
  
  onRowClick?: (id: string, row: any) => void;
  onRowDoubleClick?: (id: string, row: any) => void;
  onStateChange?: (state: TableState) => void;
  handleCheckboxClick?: (key: string) => void;
  selected?: string[];
  
  components?: {
    Toolbar?: React.ComponentType<any>;
    Row?: React.ComponentType<any>;
    Cell?: React.ComponentType<any>;
    EmptyState?: React.ComponentType<any>;
    LoadingState?: React.ComponentType<any>;
    ErrorState?: React.ComponentType<any>;
  };
  
  sx?: any;
  className?: string;
}

export enum TableFilterType {
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  DATERANGE = 'daterange',
  BOOLEAN = 'boolean',
  CUSTOM = 'custom',
  COLOR = 'color',
}

export interface ITableFilterBase {
  id: string;
  label: string;
  type: TableFilterType;
  column: string;
  placeholder?: string;
  helperText?: string;
  defaultValue?: any;
  clearable?: boolean;
  visible?: boolean;
  width?: string | number;
}

export interface ISelectFilter extends ITableFilterBase {
  type: TableFilterType.SELECT | TableFilterType.MULTISELECT;
  options: Array<{ value: any; label: string }> | ((data: any[]) => Array<{ value: any; label: string }>);
  multiple?: boolean;
}

export interface ITextFilter extends ITableFilterBase {
  type: TableFilterType.TEXT;
  debounceMs?: number;
  matchMode?: 'contains' | 'startsWith' | 'endsWith' | 'equals';
}

export interface IColorFilter extends ITableFilterBase {
  type: TableFilterType.COLOR;
}

export interface INumberFilter extends ITableFilterBase {
  type: TableFilterType.NUMBER;
  min?: number;
  max?: number;
  step?: number;
  operator?: 'equals' | 'notEquals' | 'greaterThan' | 'lessThan' | 'between';
}

export interface IDateFilter extends ITableFilterBase {
  type: TableFilterType.DATE;
  minDate?: Date;
  maxDate?: Date;
  operator?: 'equals' | 'before' | 'after' | 'between';
}

export interface IDateRangeFilter extends ITableFilterBase {
  type: TableFilterType.DATERANGE;
  minDate?: Date;
  maxDate?: Date;
}

export interface IBooleanFilter extends ITableFilterBase {
  type: TableFilterType.BOOLEAN;
  trueLabel?: string;
  falseLabel?: string;
}

export interface ICustomFilter extends ITableFilterBase {
  type: TableFilterType.CUSTOM;
  component: React.ComponentType<{
    value: any;
    onChange: (value: any) => void;
    data: any[];
  }>;
  filterFn: (row: any, value: any) => boolean;
}

export type ITableFilterType = 
  | ISelectFilter 
  | ITextFilter 
  | INumberFilter 
  | IDateFilter 
  | IDateRangeFilter 
  | IBooleanFilter 
  | IColorFilter 
  | ICustomFilter;